// Auto-generated industries data — DO NOT EDIT MANUALLY
// Slugs: fitness-wellness, financial-services, ecommerce-retail, education-tutoring
import type { Industry } from '../types';

export const industriesGroup3: Industry[] = [
  {
    "slug": "fitness-wellness",
    "name": "Fitness & Wellness",
    "icon": "Barbell",
    "tagline": "Convert More Member Inquiries. Reduce Member Churn.",
    "description": "BizRnR helps gyms, studios, and wellness centers automate lead follow-up, membership conversions, and member reactivation.",
    "longDescription": "Fitness studios, gyms, and wellness centers see their highest inquiry volume during the exact hours their staff is busiest — during classes, training sessions, and peak workout times. BizRnR ensures that every prospective member who calls receives an immediate, enthusiastic response that converts interest into a booked tour or intro session.\n\nWhen someone calls about membership options, BizRnR's AI provides pricing information, explains class schedules, answers questions about amenities and trainers, and books a facility tour or intro class. The AI is trained on your specific offerings — whether that's CrossFit, yoga, personal training, physical therapy, or spa services.\n\nMember retention is equally important. BizRnR proactively reaches out to members who haven't visited in 14+ days with a friendly check-in call, reducing membership cancellations by up to 25%. For members approaching their renewal date, the AI handles the retention conversation and processes renewals.\n\nClass and appointment scheduling becomes effortless: members call in, the AI checks availability, and books them into the right time slot. Waitlists are managed automatically — when a spot opens up, BizRnR calls the next person on the list.\n\nFor wellness practices (chiropractic, physical therapy, massage), BizRnR handles new patient intake, insurance verification questions, and follow-up appointment scheduling.",
    "painPoints": [
      {
        "title": "Intro Session No-Shows",
        "description": "Prospects book intro sessions and never show up."
      },
      {
        "title": "Cancelled Members Never Contacted",
        "description": "Members cancel and walk out forever because no one followed up."
      },
      {
        "title": "Front Desk Overloaded",
        "description": "Staff cannot answer phones, greet members, and sell simultaneously."
      }
    ],
    "solutions": [
      {
        "title": "Trial Nurture Sequences",
        "description": "AI follows up with every trial signup to maximize show rates."
      },
      {
        "title": "Win-Back Campaigns",
        "description": "Cancelled members get a personalized AI outreach sequence."
      },
      {
        "title": "Lead Response Automation",
        "description": "Every web lead gets an instant AI call to schedule their first visit."
      }
    ],
    "stats": [
      {
        "value": "3.8x",
        "label": "Higher trial show rate"
      },
      {
        "value": "29%",
        "label": "Members won back"
      },
      {
        "value": "67%",
        "label": "Faster lead response"
      }
    ],
    "useCases": [
      {
        "title": "New Year's Rush",
        "description": "January surge: BizRnR contacts every lead in 60 seconds and books visits."
      },
      {
        "title": "Lapsed Member Win-Back",
        "description": "Members cancelled 90+ days ago get a personalized AI call."
      }
    ],
    "faqs": [
      {
        "question": "Does BizRnR work with gym management software?",
        "answer": "Yes, integrates with major platforms via API."
      },
      {
        "question": "Can it sell memberships?",
        "answer": "BizRnR qualifies and nurtures; final sale happens with your staff."
      },
      {
        "question": "Can I set up class-specific flows?",
        "answer": "Yes, separate flows for yoga, CrossFit, personal training, and more."
      },
      {
        "question": "Is there a setup fee?",
        "answer": "No setup fee on AI Receptionist. Monthly subscription with a 30-day money-back guarantee."
      }
    ],
    "keywords": [
      "gym lead automation",
      "fitness studio AI",
      "member retention software"
    ]
  },
  {
    "slug": "financial-services",
    "name": "Financial Services",
    "icon": "CurrencyDollar",
    "tagline": "Respond to Every Lead. Nurture Every Prospect.",
    "description": "BizRnR helps financial advisors, accountants, and wealth managers automate prospect follow-up and client communication.",
    "longDescription": "Financial advisory firms, accounting practices, and wealth management companies operate in a trust-first industry where professionalism on the first call determines whether a prospect becomes a client. BizRnR provides financial services firms with an AI phone agent that handles prospect qualification, appointment scheduling, and client communications with the discretion and polish your practice demands.\n\nWhen a prospective client calls about financial planning, investment management, or tax preparation, BizRnR's AI conducts a professional intake: assets under management or revenue range, current advisory relationship, specific needs, and timeline. The AI qualifies prospects against your minimum thresholds before booking an introductory consultation.\n\nDuring tax season, when accounting firms are overwhelmed, BizRnR handles the surge of client calls for appointment scheduling, document request follow-ups, and extension deadline reminders. Firms using BizRnR during tax season report a 45% reduction in client complaints about unreturned calls.\n\nFor wealth management firms, BizRnR's AI handles after-hours client inquiries during market volatility, providing reassurance and scheduling urgent advisor callbacks. This level of responsiveness differentiates your firm during the moments that matter most.\n\nBizRnR integrates with Salesforce Financial Services Cloud, Wealthbox, Redtail CRM, and major practice management platforms.",
    "painPoints": [
      {
        "title": "High-Value Leads Unanswered",
        "description": "A prospect researching wealth management contacts 5 advisors simultaneously."
      },
      {
        "title": "Referrals Not Followed Up",
        "description": "Referrals go cold when follow-up is manual and inconsistent."
      },
      {
        "title": "Tax Season Overload",
        "description": "Accounting firms cannot manually respond to every inquiry during peak season."
      }
    ],
    "solutions": [
      {
        "title": "Instant Prospect Response",
        "description": "AI contacts every inquiry within 60 seconds."
      },
      {
        "title": "Referral Nurture Automation",
        "description": "Every referred prospect gets immediate personalized outreach."
      },
      {
        "title": "Seasonal Campaign Automation",
        "description": "Tax season, Q4 planning: BizRnR scales outreach without adding staff."
      }
    ],
    "stats": [
      {
        "value": "5.3x",
        "label": "More consultations booked"
      },
      {
        "value": "78%",
        "label": "Referral contact rate"
      },
      {
        "value": "43%",
        "label": "Higher close rate"
      }
    ],
    "useCases": [
      {
        "title": "Wealth Management Inquiry",
        "description": "High-net-worth prospect submits form. BizRnR calls in 60 seconds and books a consultation."
      },
      {
        "title": "Tax Season Rush",
        "description": "200 February inquiries handled efficiently by BizRnR AI."
      }
    ],
    "faqs": [
      {
        "question": "Is BizRnR compliant with financial regulations?",
        "answer": "Consult your compliance team for regulatory requirements."
      },
      {
        "question": "Can the AI discuss investment products?",
        "answer": "BizRnR handles intake and scheduling; products discussed with licensed advisors."
      },
      {
        "question": "How is sensitive financial data handled?",
        "answer": "Enterprise-grade encryption; no financial account data stored."
      },
      {
        "question": "Does it work for B2B financial services?",
        "answer": "Yes, works for both B2B and B2C."
      }
    ],
    "keywords": [
      "financial advisor AI",
      "wealth management automation",
      "accounting firm lead software"
    ]
  },
  {
    "slug": "ecommerce-retail",
    "name": "E-Commerce & Retail",
    "icon": "ShoppingCart",
    "tagline": "Recover Abandoned Carts. Reactivate Past Customers.",
    "description": "BizRnR helps e-commerce and retail businesses recover abandoned carts, reactivate dormant customers, and handle support automatically.",
    "longDescription": "E-commerce and retail businesses receive hundreds of customer inquiries daily about orders, returns, product availability, and shipping. While chatbots handle simple questions, complex issues require a human touch — or in BizRnR's case, an AI voice agent that sounds indistinguishable from your best customer service representative.\n\nBizRnR handles inbound customer calls about order status, return processing, product questions, and billing issues. The AI accesses your order management system to provide real-time shipping updates, initiate returns, and escalate complex issues to your team with full context. Customers get resolution in minutes instead of waiting on hold.\n\nFor outbound campaigns, BizRnR drives revenue through abandoned cart recovery calls. When a customer adds items to their cart but doesn't complete checkout, BizRnR calls with a personalized message — offering to help complete the order, answer questions, or provide a limited-time discount. This recovers 8-15% of abandoned carts that email alone would miss.\n\nPost-purchase follow-up calls drive reviews and repeat purchases. BizRnR calls customers 7 days after delivery to check satisfaction, request product reviews, and recommend complementary items.\n\nFor brick-and-mortar retailers with e-commerce, BizRnR handles store hours inquiries, product availability checks, and in-store pickup scheduling.",
    "painPoints": [
      {
        "title": "70% Cart Abandonment",
        "description": "7 in 10 carts are abandoned. Most retailers never follow up."
      },
      {
        "title": "One-Time Buyers Never Return",
        "description": "First-time customers have a 27% return chance without nurturing."
      },
      {
        "title": "Support Volume Overwhelms Staff",
        "description": "Order status and returns flood your team during peak seasons."
      }
    ],
    "solutions": [
      {
        "title": "Cart Abandonment Recovery",
        "description": "AI follows up via SMS and voice within minutes of abandonment."
      },
      {
        "title": "Customer Reactivation Campaigns",
        "description": "AI identifies dormant customers and delivers re-engagement offers."
      },
      {
        "title": "Post-Purchase Support Automation",
        "description": "Order updates and FAQs handled by AI, freeing your team."
      }
    ],
    "stats": [
      {
        "value": "23%",
        "label": "Cart recovery rate"
      },
      {
        "value": "2.1x",
        "label": "Repeat purchase rate"
      },
      {
        "value": "64%",
        "label": "Support tickets deflected"
      }
    ],
    "useCases": [
      {
        "title": "Black Friday Cart Recovery",
        "description": "Customer abandons a cart. BizRnR sends SMS in 10 minutes and calls at 1 hour."
      },
      {
        "title": "90-Day Win-Back",
        "description": "Customers silent for 90 days get a personalized offer."
      }
    ],
    "faqs": [
      {
        "question": "Does BizRnR integrate with Shopify?",
        "answer": "Yes, integrates with Shopify, WooCommerce, and more."
      },
      {
        "question": "Can AI handle return requests?",
        "answer": "Yes, initiates return workflows and provides status updates."
      },
      {
        "question": "Is SMS outreach TCPA compliant?",
        "answer": "BizRnR follows opt-in best practices; ensure your list is consented."
      },
      {
        "question": "Can it be personalized by category?",
        "answer": "Yes, segment by product type, history, and customer value."
      }
    ],
    "keywords": [
      "ecommerce AI",
      "cart abandonment recovery",
      "retail automation software"
    ]
  },
  {
    "slug": "education-tutoring",
    "name": "Education & Tutoring",
    "icon": "Books",
    "tagline": "Enroll More Students. Reduce Drop-Off.",
    "description": "BizRnR helps tutoring centers, online courses, and educational services automate enrollment follow-up and reactivation.",
    "longDescription": "Education companies, tutoring centers, and training providers face intense competition for enrollment. Parents researching tutoring for their children call 3-4 providers and enroll with the first one that responds professionally and makes scheduling easy. BizRnR ensures your education business captures every inquiry and converts it into an enrolled student.\n\nBizRnR's AI conducts a thorough intake when parents or students call: current grade level, subjects of concern, learning goals, schedule availability, and budget. Based on these inputs, the AI recommends the appropriate program and schedules an assessment or trial session. The conversational approach builds trust from the first interaction.\n\nFor tutoring centers with multiple subjects and instructors, BizRnR matches students to the right tutor based on subject, grade level, and scheduling compatibility. Group class enrollment is handled seamlessly — the AI checks availability, processes registration, and sends confirmation details.\n\nRetention campaigns keep students enrolled: BizRnR calls families before each academic term to confirm continued enrollment, discuss progress, and upsell additional subjects or summer programs. This proactive approach reduces student attrition by 30%.\n\nProfessional training companies and trade schools use BizRnR to handle prospective student inquiries about programs, admissions requirements, financial aid, and campus tours.",
    "painPoints": [
      {
        "title": "Inquiry to Enrollment Drop-Off",
        "description": "Prospective students inquire but never enroll."
      },
      {
        "title": "Trial Class No-Shows",
        "description": "Free trial classes are booked and forgotten."
      },
      {
        "title": "Lapsed Students Not Recovered",
        "description": "Students who stopped attending are rarely contacted."
      }
    ],
    "solutions": [
      {
        "title": "Enrollment Nurture Sequences",
        "description": "AI follows up with every inquiry to guide toward enrollment."
      },
      {
        "title": "Trial Class Reminders",
        "description": "Automated voice and SMS reminders maximize attendance."
      },
      {
        "title": "Re-Enrollment Campaigns",
        "description": "Past students receive personalized AI outreach each term."
      }
    ],
    "stats": [
      {
        "value": "3.2x",
        "label": "Higher enrollment rate"
      },
      {
        "value": "81%",
        "label": "Trial class attendance"
      },
      {
        "value": "34%",
        "label": "Lapsed student recovery"
      }
    ],
    "useCases": [
      {
        "title": "Back-to-School Drive",
        "description": "August surge: BizRnR contacts every lead and guides them through enrollment."
      },
      {
        "title": "Summer Program Re-Enrollment",
        "description": "Last year's students get AI outreach in April to re-enroll."
      }
    ],
    "faqs": [
      {
        "question": "Does it work for online and in-person tutoring?",
        "answer": "Yes, works for both models."
      },
      {
        "question": "Can it handle K-12 and adult education?",
        "answer": "Yes, configure age-appropriate messaging."
      },
      {
        "question": "Can AI answer curriculum questions?",
        "answer": "BizRnR answers FAQs and escalates detailed questions."
      },
      {
        "question": "Is there a student data privacy concern?",
        "answer": "BizRnR does not store minor student data, following COPPA guidelines."
      }
    ],
    "keywords": [
      "tutoring automation",
      "education enrollment AI",
      "student follow-up software"
    ]
  }
];
